import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Clock, BarChart3, TrendingUp, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { PaymentsReceipts } from "@/components/PaymentsReceipts";
import { AccountBalances } from "@/components/AccountBalances";
import { ForecastChart } from "@/components/ForecastChart";
import { FinancialHealthCard } from "@/components/FinancialHealthCard";

const Landing = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, login } = useAuth();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha email e senha para continuar.",
        variant: "destructive",
      });
      return;
    }
    login();
    toast({
      title: "Login realizado com sucesso!",
      description: "Redirecionando para o dashboard...",
    });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    document.title = "Radar Financeiro | Monitore sua rota financeira";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Transforme sua vida financeira com o Radar Financeiro. Análise completa, dashboard inteligente e crescimento garantido.');
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header - só mostra se estiver logado */}
      {isAuthenticated && <Header />}

      {/* Header fixo para usuário não logado */}
      {!isAuthenticated && (
        <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold text-foreground">Radar Financeiro</span>
              </div>
              
              <nav className="flex items-center gap-6">
                <button
                  onClick={() => scrollToSection('hero')}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Início
                </button>
                <button
                  onClick={() => scrollToSection('planos')}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Planos
                </button>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main Content - Different layout for authenticated users */}
      {!isAuthenticated ? (
        <>
          {/* Hero Section with Login Form */}
          <main id="hero" className="pt-24 pb-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Left Side - Hero Content */}
                <div className="space-y-6 text-center lg:text-left">
                  <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
                    Monitore sua rota financeira.
                  </h1>
                  <p className="text-lg md:text-xl text-muted-foreground">
                    Comece agora a transformar sua vida financeira com análises completas e relatórios em tempo real.
                  </p>
                </div>

                {/* Right Side - Login Form */}
                <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium">
                        Senha
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12"
                        required
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate('/auth')}
                      className="text-sm text-primary hover:underline"
                    >
                      Esqueci minha senha
                    </button>

                    <Button type="submit" className="w-full h-12 text-base font-semibold">
                      Entrar
                    </Button>

                    <div className="text-center pt-2">
                      <button
                        type="button"
                        onClick={() => navigate('/auth')}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Não tem conta? <span className="font-medium text-primary">Cadastre-se</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </main>

          {/* Planos Section */}
          <section id="planos" className="py-16 px-4 bg-muted/30">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Escolha seu Plano
                </h2>
                <p className="text-lg text-muted-foreground">
                  Planos flexíveis para diferentes necessidades financeiras.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Plano Básico */}
                <div className="bg-card border border-border rounded-lg p-8 text-center space-y-6 hover:shadow-lg transition-shadow">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Básico</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-foreground">Grátis</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Até 50 lançamentos/mês</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Dashboard básico</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Relatórios mensais</span>
                    </li>
                  </ul>

                  <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                    Começar Grátis
                  </Button>
                </div>

                {/* Plano Profissional */}
                <div className="bg-primary text-primary-foreground border-2 border-primary rounded-lg p-8 text-center space-y-6 relative hover:shadow-xl transition-shadow">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-foreground text-background px-4 py-1 rounded-full text-xs font-bold">
                    MAIS POPULAR
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Profissional</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold">R$ 29</span>
                      <span className="text-sm opacity-90">/mês</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm opacity-90">Lançamentos ilimitados</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm opacity-90">Dashboard avançado</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm opacity-90">Relatórios em tempo real</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm opacity-90">Suporte prioritário</span>
                    </li>
                  </ul>

                  <Button variant="secondary" className="w-full" onClick={() => navigate('/auth')}>
                    Assinar Agora
                  </Button>
                </div>

                {/* Plano Empresarial */}
                <div className="bg-card border border-border rounded-lg p-8 text-center space-y-6 hover:shadow-lg transition-shadow">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Empresarial</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-foreground">R$ 99</span>
                      <span className="text-sm text-muted-foreground">/mês</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Tudo do Profissional</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Multi-usuários</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">API de integração</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Consultoria dedicada</span>
                    </li>
                  </ul>

                  <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                    Falar com Vendas
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        // Dashboard for authenticated users
        <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">
            <div className="space-y-6">
              <FinancialHealthCard />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PaymentsReceipts />
                <AccountBalances />
              </div>
              <ForecastChart />
            </div>
          </div>
        </main>
      )}

      {/* Funcionalidades Section - Only for non-authenticated users */}
      {!isAuthenticated && (
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Funcionalidades
              </h2>
              <p className="text-lg text-muted-foreground">
                Tudo que você precisa para manter suas finanças organizadas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center space-y-4 p-6 rounded-lg bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Análise Completa</h3>
                <p className="text-muted-foreground">
                  Organize suas receitas por plano de contas com gráficos intuitivos e relatórios detalhados.
                </p>
              </div>

              <div className="text-center space-y-4 p-6 rounded-lg bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <BarChart3 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Dashboard Inteligente</h3>
                <p className="text-muted-foreground">
                  Acompanhe receitas, despesas e saldo em tempo real com indicadores visuais.
                </p>
              </div>

              <div className="text-center space-y-4 p-6 rounded-lg bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <TrendingUp className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Crescimento</h3>
                <p className="text-muted-foreground">
                  Identifique tendências e oportunidades para melhorar sua saúde financeira.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 text-center border-t">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-6 h-6 bg-primary rounded-sm flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-foreground">Radar Financeiro</span>
        </div>
        <p className="text-sm text-muted-foreground">
          © 2025 Radar Financeiro. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
};

export default Landing;